import { ErrorDeAcceso } from './errores';
import type { PuertoDeIdentidad, Sesion, Usuario } from './puerto';

/**
 * El adaptador de verdad: habla con `capital`, el backend en las cuentas de
 * Focalius.
 *
 * Implementa el mismo puerto que `AdaptadorLocal`, así que **ninguna pantalla
 * cambia** al montarlo: lo único que notan es que `esDeMentira` pasa a `false` y
 * el aviso rojo se apaga solo.
 *
 * ## El token es opaco aquí
 *
 * **Este archivo no verifica el JWT.** Quien lo valida es el authorizer de
 * `capital`, contra el JWKS, en el servidor. Verificar una firma en el
 * navegador no prueba nada —el navegador es de quien lo mira— y da la sensación
 * contraria. Lo único que se lee del token es cuándo caduca, y eso llega como
 * un número aparte en la respuesta, sin tocar el token.
 *
 * ## Dónde vive la sesión, y lo que cuesta
 *
 * En `localStorage`, no en una cookie. No es preferencia: `ilumetria.com` y
 * `focalius.dev` son dominios registrables distintos, así que una cookie de
 * sesión sería de tercera parte y Safari y Chrome la bloquean.
 *
 * El precio, dicho en voz alta: **un XSS la lee**. Lo que lo acota es que el
 * token dura diez minutos.
 */

const CLAVE = 'ilumetria.inversionistas.sesion';

interface SesionGuardada {
  usuario: Usuario;
  expiraEn: number;
  token: string;
}

function leer(): SesionGuardada | null {
  try {
    const crudo = window.localStorage.getItem(CLAVE);
    if (!crudo) return null;
    const dato = JSON.parse(crudo) as SesionGuardada;
    if (typeof dato?.token !== 'string' || typeof dato?.expiraEn !== 'number') return null;
    if (dato.expiraEn <= Date.now()) {
      borrar();
      return null;
    }
    return dato;
  } catch {
    return null;
  }
}

function guardar(s: SesionGuardada): void {
  try {
    window.localStorage.setItem(CLAVE, JSON.stringify(s));
  } catch {
    /* sin almacenamiento la sesión vive sólo en memoria */
  }
}

function borrar(): void {
  try {
    window.localStorage.removeItem(CLAVE);
  } catch {
    /* ídem */
  }
}

/** Traduce lo que devuelve la API a los códigos del puerto. */
function codigoDeRespuesta(estado: number, cuerpo: unknown): string {
  const code = (cuerpo as { code?: string } | null)?.code;
  if (estado === 401) return 'credenciales';
  if (estado === 429) return 'credenciales';
  if (code === 'faltan-campos' || code === 'malformado') return 'generico';
  if (estado >= 500) return 'servidor';
  return 'generico';
}

export class AdaptadorHttp implements PuertoDeIdentidad {
  readonly esDeMentira = false;

  #base: string;
  #enMemoria: Usuario | null = leer()?.usuario ?? null;

  constructor(base: string) {
    // Sin barra final: se concatena con rutas que empiezan por `/`.
    this.#base = base.replace(/\/+$/, '');
  }

  async iniciarSesion(correo: string, contrasena: string): Promise<Sesion> {
    if (!this.#base) throw new ErrorDeAcceso('sinConfigurar');

    let respuesta: Response;
    try {
      respuesta = await fetch(`${this.#base}/acceso/entrar`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        // El token no va en cookie, así que no hay credenciales que mandar.
        credentials: 'omit',
        body: JSON.stringify({ correo: correo.trim(), contrasena }),
      });
    } catch (error) {
      // `fetch` sólo rechaza por red, DNS o CORS. Un 500 NO rechaza.
      throw new ErrorDeAcceso('red', error);
    }

    const cuerpo: unknown = await respuesta.json().catch(() => null);
    if (!respuesta.ok) {
      throw new ErrorDeAcceso(
        codigoDeRespuesta(respuesta.status, cuerpo) as 'credenciales',
        cuerpo,
      );
    }

    const datos = cuerpo as {
      token: string;
      expiraEn: number;
      usuario: { id: string; correo: string; nombre: string | null; rol?: string };
    };
    const sesion: SesionGuardada = {
      // `?? 'inversionista'` cubre a un servidor anterior a la migracion
      // `0002_rol`. El defecto es el que menos puede.
      usuario: { ...datos.usuario, rol: datos.usuario.rol ?? 'inversionista' },
      // El servidor manda **segundos**; aquí se guarda el instante.
      expiraEn: Date.now() + datos.expiraEn * 1000,
      token: datos.token,
    };
    guardar(sesion);
    this.#enMemoria = sesion.usuario;
    return { usuario: sesion.usuario, expiraEn: sesion.expiraEn };
  }

  async pedirCodigo(correo: string): Promise<void> {
    // 202 y siempre el mismo cuerpo, exista o no la cuenta. Este adaptador no
    // interpreta: lo que el servidor no dice, aquí tampoco se inventa.
    await this.#postPublico('/acceso/recuperar', { correo: correo.trim().toLowerCase() });
  }

  async restablecer(correo: string, codigo: string, contrasena: string): Promise<void> {
    await this.#postPublico('/acceso/restablecer', {
      correo: correo.trim().toLowerCase(),
      codigo,
      contrasena,
    });
  }

  /** Un POST sin token, con los errores del servidor traducidos a códigos. */
  async #postPublico(ruta: string, cuerpo: unknown): Promise<unknown> {
    let respuesta: Response;
    try {
      respuesta = await fetch(`${this.#base}${ruta}`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(cuerpo),
      });
    } catch (causa) {
      throw new ErrorDeAcceso('red', causa);
    }

    let datos: { error?: string } = {};
    try {
      datos = (await respuesta.json()) as { error?: string };
    } catch {
      // Un cuerpo que no es JSON. Si la respuesta era buena, da igual.
      if (respuesta.ok) return {};
      throw new ErrorDeAcceso('servidor');
    }

    if (respuesta.ok) return datos;

    // El mapa está aquí y no en la pantalla: es el adaptador quien conoce el
    // vocabulario del servidor, y la pantalla sólo habla de códigos.
    const porCodigo: Record<string, 'codigoInvalido' | 'codigoCaducado' | 'contrasenaCorta'> = {
      'codigo-invalido': 'codigoInvalido',
      'codigo-caducado': 'codigoCaducado',
      'contrasena-corta': 'contrasenaCorta',
    };
    const conocido = datos.error ? porCodigo[datos.error] : undefined;
    if (conocido) throw new ErrorDeAcceso(conocido);
    throw new ErrorDeAcceso(respuesta.status >= 500 ? 'servidor' : 'generico');
  }

  async cerrarSesion(): Promise<void> {
    // No hay petición: el token caduca solo en diez minutos, y revocar de
    // verdad es marcar el refresh en la base, que es otra fase. Borrar aquí es
    // lo que el usuario espera de «cerrar sesión» en este navegador.
    borrar();
    this.#enMemoria = null;
  }

  async sesionActual(): Promise<Sesion | null> {
    const guardada = leer();
    this.#enMemoria = guardada?.usuario ?? null;
    return guardada ? { usuario: guardada.usuario, expiraEn: guardada.expiraEn } : null;
  }

  usuario(): Usuario | null {
    return this.#enMemoria;
  }

  async credencial(): Promise<string | null> {
    return leer()?.token ?? null;
  }
}
